import React from 'react';
import { useTranslation } from 'react-i18next';
import { PurchasableProduct } from '../../types/customer';
import { formatCurrency } from '../../utils/currency';

/**
 * The recurring-purchase disclosure, shown under the payment button.
 *
 * Japan's 特定商取引法 was amended in June 2022 with rules aimed at 定期購入.
 * Before the order is placed, the final screen has to state 分量 / 販売価格 /
 * 支払時期・方法 / 引渡時期 / 申込みの撤回・解除. All five are here, written as
 * one paragraph rather than a table.
 *
 * QUIET ON PURPOSE. There is no tick box and no coloured panel: the statute
 * requires the information to be PRESENT and not misleading, not that it be
 * confirmed with a gesture. A boxed table above the button competed with the
 * price for attention and made an ordinary subscription feel like a contract
 * signing. Below the button, in the same grey as the rest of the fine print, is
 * where a reader expects terms — and it is still on the screen where the order
 * is placed, which is the part that matters.
 *
 * EVERY CLAIM IS DERIVED, never asserted. The allowance, amount and interval
 * come from the product being bought; the payment timing, availability and
 * cancellation behaviour describe what this system actually does — charge on
 * subscribe through Stripe, usable immediately, cancel at period end with no
 * proration. Nothing states a tax treatment, because a host types a bare number
 * and we do not model tax; claiming 税込 would invent a fact about someone
 * else's pricing.
 *
 * ⚠️ NOT A COMPLETE 特商法 SURFACE. The statute also requires each SELLER's own
 * 特定商取引法に基づく表記 — legal name, address, contact, their own return
 * policy. That belongs to the host and this product has nowhere to put it yet.
 */

interface SubscriptionTermsProps {
  product: PurchasableProduct;
  /** The host or organization selling this, when the surface knows it. */
  sellerName?: string | null;
  className?: string;
}

const SubscriptionTerms: React.FC<SubscriptionTermsProps> = ({
  product,
  sellerName,
  className = '',
}) => {
  const { t } = useTranslation();

  const price =
    product.price != null ? formatCurrency(Number(product.price), product.currency) : '—';

  const allowance =
    product.allowanceCount == null
      ? t('booking:terms.allowanceUnlimited', 'unlimited bookings')
      : t('booking:terms.allowance', '{{count}} bookings per {{period}}', {
          count: product.allowanceCount,
          period: t(`booking:period.${product.allowancePeriod ?? 'month'}`, 'month'),
        });

  return (
    <p
      // Justified, with Japanese line-breaking. A long paragraph of Japanese
      // set ragged-right breaks at awkward places and looked uneven; `justify`
      // plus `break-strict` fills each line edge to edge and keeps the breaks
      // where the script expects them.
      className={`text-xs leading-relaxed text-gray-400 text-justify [text-wrap:pretty] ${className}`}
      style={{ wordBreak: 'break-word', lineBreak: 'strict' }}
    >
      {sellerName
        ? t('booking:terms.bodyWithSeller', {
            seller: sellerName,
            allowance,
            price,
            defaultValue:
              'Sold by {{seller}}. This plan gives you {{allowance}} and renews automatically every month until you cancel. {{price}} is charged to your card now and on the same date each month, and you can use it as soon as payment completes. You can cancel at any time from your account page; you keep access until the end of the period you have already paid for, and that period is not refunded pro rata.',
          })
        : t('booking:terms.body', {
            allowance,
            price,
            defaultValue:
              'This plan gives you {{allowance}} and renews automatically every month until you cancel. {{price}} is charged to your card now and on the same date each month, and you can use it as soon as payment completes. You can cancel at any time from your account page; you keep access until the end of the period you have already paid for, and that period is not refunded pro rata.',
          })}
    </p>
  );
};

export default SubscriptionTerms;
